// Sekce Nabídky – seznam se sdíleným toolbar/kartami z shared/list-ui.js.
// Stránkovaný list endpoint (limit/offset) vrací ořezané záznamy vč. customerName.
import { icon } from './icons.js';
import {
  API_BASE_URL, ListView, renderListCard, renderErrorState,
  escapeHtml, formatDateTime, formatMoney, customerNameOf, STATUS_META
} from './shared/list-ui.js';

const OFFER_STATUS_ORDER = ['draft', 'sent', 'negotiation', 'approved', 'rejected', 'converted'];

export class OffersListManager {
  constructor() {
    this.offers = [];
    this.view = new ListView({
      key: 'offers',
      title: 'Nabídky',
      subtitle: 'Stav nabídek a jejich převod na objednávky.',
      searchPlaceholder: 'Hledat (číslo, firma, položka)…',
      statusKind: 'offer',
      statuses: OFFER_STATUS_ORDER,
      authorLabels: { me: 'Moje nabídky' },
      enableCustomerFilter: true,
      enableKanban: true,
      onChange: () => this.loadOffers()
    });
    this.init();
  }

  init() {
    this.loadOffers();
  }

  async loadOffers() {
    const container = document.getElementById('nabidky-section');
    if (!container) return;
    if (!document.getElementById('offers-list')) {
      this.view.renderShell(container);
      this._bindListEvents();
    } else {
      this.view.renderLoading();
    }

    try {
      if (this.view.viewMode === 'kanban') {
        await this.loadKanban();
        return;
      }
      const params = this.view.buildParams();
      const response = await fetch(`${API_BASE_URL}/offers?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Chyba při načítání nabídek');
      const result = await response.json();
      this.offers = result.data || [];
      if (Array.isArray(result.statuses) && result.statuses.length) this.view.statuses = result.statuses;
      if (Array.isArray(result.authors)) this.view.authors = result.authors;

      this.view.renderResults({
        rowsHtml: this.offers.map((o) => this.renderOfferRow(o)),
        total: result.total,
        emptyState: {
          title: 'Zatím nemáte žádné nabídky',
          hint: 'Vytvořte první nabídku v sekci Kalkulátor přidáním položek a kliknutím na „Vytvořit nabídku“.'
        }
      });
    } catch (error) {
      console.error('Chyba při načítání nabídek:', error);
      this.view.renderCustom(renderErrorState('Nepodařilo se načíst nabídky', 'window.offersListManager.loadOffers()'));
    }
  }

  async loadKanban() {
    const { renderKanbanBoard } = await import('./shared/kanban.js');
    await renderKanbanBoard({
      view: this.view,
      statusKind: 'offer',
      fetchColumn: async (status) => {
        const params = this.view.buildParams({ paginate: false });
        params.set('status', status);
        params.set('limit', '30');
        params.set('sort', 'kanban');
        const res = await fetch(`${API_BASE_URL}/offers?${params}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Chyba při načítání nabídek');
        const result = await res.json();
        return { items: result.data || [], total: result.total || 0 };
      },
      cardData: (o) => ({
        id: o.id,
        title: `#${o.id}`,
        customerName: customerNameOf(o),
        meta: formatDateTime(o.createdAt),
        price: formatMoney(o.totalPrice),
        draggable: o.status !== 'converted',
        orderKey: typeof o.kanbanOrder === 'number' ? o.kanbanOrder : Date.parse(o.createdAt)
      }),
      onOpen: (id) => this.openOfferEditor(id),
      onDrop: async (id, { status, statusChanged, orderKey }) => {
        if (statusChanged) {
          const res = await fetch(`${API_BASE_URL}/offers/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ status, note: 'Změna stavu přetažením v kanbanu' })
          });
          const result = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(result.error || 'Změna stavu se nezdařila');
        }
        // Ruční pořadí ve sloupci (kanbanOrder ukládá updateOffer přes PUT)
        const res2 = await fetch(`${API_BASE_URL}/offers/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ kanbanOrder: orderKey })
        });
        if (!res2.ok) {
          const result = await res2.json().catch(() => ({}));
          throw new Error(result.error || 'Uložení pořadí se nezdařilo');
        }
      }
    });
  }

  renderOfferRow(offer) {
    const itemsCount = offer.itemsCount != null ? offer.itemsCount : (offer.items || []).length;
    const itemsText = itemsCount === 1 ? '1 položka' : `${itemsCount} položek`;
    const itemNames = (offer.items || []).map((i) => escapeHtml(i.name)).join(', ');
    const badges = [];
    if (offer.convertedToOrderId) {
      badges.push(`
        <span class="inline-flex items-center gap-0.5 text-xs text-purple-700">
          ${icon('arrow-right', 'w-3 h-3')}
          <button type="button" class="underline hover:no-underline" data-action="open-order" data-id="${escapeHtml(offer.convertedToOrderId)}" title="Otevřít objednávku ${escapeHtml(offer.convertedToOrderId)}">${escapeHtml(offer.convertedToOrderId)}</button>
        </span>
      `);
    }

    return renderListCard({
      id: offer.id,
      title: `Nabídka <span class="font-semibold">#${escapeHtml(offer.id)}</span>`,
      status: { kind: 'offer', value: offer.status || 'draft' },
      customerName: customerNameOf(offer),
      metaLine: `
        <span class="inline-flex items-center">${icon('calendar', 'w-3.5 h-3.5 mr-1 text-gray-400')}${formatDateTime(offer.createdAt)}</span>
        <span>·</span><span>${itemsText}</span>
        ${offer.createdByName ? `<span>·</span><span>${escapeHtml(offer.createdByName)}</span>` : ''}
      `,
      description: itemNames,
      price: formatMoney(offer.totalPrice),
      priceNote: 'bez DPH',
      badges,
      compact: this.view.compact,
      actions: [
        { action: 'open', icon: 'eye', label: 'Otevřít nabídku', tone: 'primary' },
        { action: 'load', icon: 'calculator', label: 'Načíst do kalkulátoru a upravit' },
        { action: 'delete', icon: 'trash', label: 'Smazat nabídku', tone: 'danger' }
      ]
    });
  }

  _bindListEvents() {
    const list = this.view.listContainer;
    if (!list) return;
    list.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const { action, id } = actionBtn.dataset;
        if (action === 'open') this.openOfferEditor(id);
        else if (action === 'load') this.editOffer(id);
        else if (action === 'delete') this.deleteOffer(id);
        else if (action === 'open-order') window.ordersListManager?.openDetail(id);
        return;
      }
      const row = e.target.closest('[data-row-id]');
      if (row && !e.target.closest('button, a')) {
        this.openOfferEditor(row.dataset.rowId);
      }
    });
  }

  async editOffer(offerId) {
    try {
      const response = await fetch(`${API_BASE_URL}/offers/${offerId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Chyba při načítání nabídky');
      const result = await response.json();
      const offer = result.data;

      if (window.sectionManager) window.sectionManager.showSection('kalkulator');

      if (window.offerItemsManager) {
        window.offerItemsManager.items = [];
        window.offerItemsManager.itemIdCounter = 0;
        window.offerItemsManager.activeItemId = null;

        offer.items.forEach((item, index) => {
          if (index === 0) {
            window.offerItemsManager.addItem();
            const firstItem = window.offerItemsManager.items[0];
            firstItem.name = item.name;
            firstItem.formData = item.formData;
            firstItem.price = item.price;
            firstItem.pricePerPiece = item.pricePerPiece;
          } else {
            const itemId = ++window.offerItemsManager.itemIdCounter;
            window.offerItemsManager.items.push({
              id: itemId,
              name: item.name,
              formData: item.formData,
              price: item.price,
              pricePerPiece: item.pricePerPiece
            });
          }
        });

        if (window.offerItemsManager.items.length > 0) {
          window.offerItemsManager.activeItemId = window.offerItemsManager.items[0].id;
          window.offerItemsManager.restoreFormState(window.offerItemsManager.items[0].formData);
          window.offerItemsManager.renderItems();
          window.offerItemsManager.updateTotalPrice();
        }
      }

      if (window.showNotification) window.showNotification('Nabídka načtena do kalkulátoru', 'success');
    } catch (error) {
      console.error('Chyba při editaci nabídky:', error);
      if (window.showNotification) window.showNotification('Chyba při načítání nabídky', 'error');
    }
  }

  async deleteOffer(offerId) {
    if (!confirm('Opravdu chcete smazat tuto nabídku?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/offers/${offerId}`, { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error('Chyba při mazání nabídky');
      if (window.showNotification) window.showNotification('Nabídka byla úspěšně smazána', 'success');
      this.loadOffers();
    } catch (error) {
      console.error('Chyba při mazání nabídky:', error);
      if (window.showNotification) window.showNotification('Chyba při mazání nabídky', 'error');
    }
  }

  async openOfferEditor(offerId) {
    try {
      const response = await fetch(`${API_BASE_URL}/offers/${offerId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Chyba při načítání nabídky');
      const result = await response.json();
      const offer = result.data;

      if (window.sectionManager) window.sectionManager.showSection('nabidka-editor');
      if (window.offerEditorManager) window.offerEditorManager.loadOffer(offer);
    } catch (error) {
      console.error('Chyba při otevírání editoru nabídky:', error);
      if (window.showNotification) window.showNotification('Chyba při otevírání editoru nabídky', 'error');
    }
  }

  refresh() {
    this.loadOffers();
  }

  escape(str) {
    return escapeHtml(str);
  }
}

OffersListManager.STATUS_META = STATUS_META.offer;
OffersListManager.STATUS_ORDER = OFFER_STATUS_ORDER;

window.OffersListManager = OffersListManager;
